import { createSelector } from '@reduxjs/toolkit';
import { rootStore } from '../Store';
import type { RootState } from '../Store';
import { GridBlockType } from 'services/entities/BlockTypes';
import { TextDefaultSelectorsEnum } from '../textDefaultStylingSlice';
import type { TextDefaultTagSelectors, TextDefaultStylesState, TableTextDefaultStylesState } from '../textDefaultStylingSlice';
import type { BlockSettings } from '../blockStyleSettingsSlice';
import type { TableRowStyleState } from '../tableRowDefaultStyleSlice';
import type { JSONContent } from '@tiptap/core';
import { ColorModel } from '../../../shared/components/ColorPicker/colorModel';
import { DEFAULT_BLOCK_BACKGROUND_COLOR, DEFAULT_DOCUMENT_BACKGROUND_COLOR } from '../../../shared/documentColorDefaults';
const MAX_COLOUR_CACHE_SIZE = 200;
const MAX_TIP_TAP_CACHE_SIZE = 200;

const normalizeCache = new Map<string, string>();

function normalizeColor(color: string): string {
  const cached = normalizeCache.get(color);
  if (cached) return cached;

  const result = ColorModel.toColorSet(color, true).hex;

  if (normalizeCache.size >= MAX_COLOUR_CACHE_SIZE) {
    const oldestKey = normalizeCache.keys().next().value;
    if (oldestKey) normalizeCache.delete(oldestKey);
  }

  normalizeCache.set(color, result);
  return result;
}

const BLOCK_TYPES_WITH_BACKGROUND: ReadonlySet<GridBlockType> = new Set([GridBlockType.TEXT, GridBlockType.SHAPE]);
const BLOCK_TYPES_WITH_BORDER: ReadonlySet<GridBlockType> = new Set([GridBlockType.TEXT, GridBlockType.SHAPE, GridBlockType.IMAGE]);

type ColorEntry = string | { nodeType: TextDefaultTagSelectors };
type TipTapParseResult = { entries: ColorEntry[] };

function collectTipTapTextData(node: JSONContent, result: TipTapParseResult, nodeType: TextDefaultTagSelectors): void {
  if (node.type !== 'text') return;
  let hasColor = false;
  if (Array.isArray(node.marks)) {
    for (const mark of node.marks) {
      if (mark.type === 'textStyle' && typeof mark.attrs?.color === 'string') {
        result.entries.push(mark.attrs.color);
        hasColor = true;
      }
    }
  }
  if (!hasColor) {
    result.entries.push({ nodeType: nodeType });
  }
}

function collectTipTapNodeData(node: JSONContent, result: TipTapParseResult): void {
  if (node.type === 'paragraph') {
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        collectTipTapTextData(child, result, TextDefaultSelectorsEnum.NORMAL);
      }
    }
  } else if (node.type === 'heading') {
    const level = node.attrs?.level;
    if (level >= 1 && level <= 4 && Array.isArray(node.content)) {
      const nodeType = `h${level}` as TextDefaultTagSelectors;
      for (const child of node.content) {
        collectTipTapTextData(child, result, nodeType);
      }
    }
  } else if (Array.isArray(node.content)) {
    for (const child of node.content) {
      collectTipTapNodeData(child, result);
    }
  }
}

const tipTapCache = new Map<string, TipTapParseResult>();

function extractTipTapData(jsonContent: string): TipTapParseResult {
  const cached = tipTapCache.get(jsonContent);
  if (cached) return cached;

  // if a block ever has malformed content for any reason (migration, bug, partial save)
  // it silently skips it rather than crashing the entire color picker on open.
  const result: TipTapParseResult = { entries: [] };
  try {
    collectTipTapNodeData(JSON.parse(jsonContent), result);
  } catch {
    // invalid JSON — return empty result
  }

  if (tipTapCache.size >= MAX_TIP_TAP_CACHE_SIZE) {
    const oldestKey = tipTapCache.keys().next().value;
    if (oldestKey) tipTapCache.delete(oldestKey);
  }

  tipTapCache.set(jsonContent, result);
  return result;
}

function collectBlockStyleColors(type: GridBlockType, style: BlockSettings, seen: Set<string>): void {
  // Do not include the default transparent block background color.
  if (BLOCK_TYPES_WITH_BACKGROUND.has(type) && style.backgroundColor && style.backgroundColor !== DEFAULT_BLOCK_BACKGROUND_COLOR) {
    seen.add(normalizeColor(style.backgroundColor));
  }
  if (BLOCK_TYPES_WITH_BORDER.has(type) && style.borderColor) {
    seen.add(normalizeColor(style.borderColor));
  }
}

function collectTipTapColors(
  content: string,
  textDefaults: TextDefaultStylesState,
  seenNodeTypes: Set<TextDefaultTagSelectors>,
  seen: Set<string>
): void {
  const { entries } = extractTipTapData(content);
  for (const entry of entries) {
    if (typeof entry === 'string') {
      seen.add(normalizeColor(entry));
    } else {
      // Add text default style colors for node types present in the document,
      // only when the color has not been overridden by inline styles.
      const { nodeType } = entry;
      if (!seenNodeTypes.has(nodeType)) {
        seenNodeTypes.add(nodeType);
        const defaultColor = textDefaults[nodeType]?.color;
        if (typeof defaultColor === 'string') seen.add(normalizeColor(defaultColor));
      }
    }
  }
}

function collectTableColors(tableTextDefaults: TableTextDefaultStylesState, tableRowStyles: TableRowStyleState, seen: Set<string>): void {
  for (const rowStyle of Object.values(tableTextDefaults)) {
    if (typeof rowStyle.color === 'string') seen.add(normalizeColor(rowStyle.color));
  }
  for (const rowStyle of Object.values(tableRowStyles)) {
    const bgColor = rowStyle['--table-row-default-background-color'];
    if (bgColor) seen.add(normalizeColor(bgColor));
  }
}

export const selectDocumentColors = createSelector(
  (state: RootState) => state.gridBlockReducer.sections,
  (state: RootState) => state.blockStyleSettings.blockStyle,
  (state: RootState) => state.allTextDefaultStylesReducer.textDefaultStyles,
  (state: RootState) => state.allTextDefaultStylesReducer.tableTextDefaultStyles,
  (state: RootState) => state.tableRowDefaultStyleReducer,
  (state: RootState) => state.documentSettings.backgroundColor,
  (sections, blockStyles, textDefaults, tableTextDefaults, tableRowStyles, documentBackgroundColor) => {
    const seen = new Set<string>();
    const seenNodeTypes = new Set<TextDefaultTagSelectors>();

    if (documentBackgroundColor && !ColorModel.isColorEqual(documentBackgroundColor, DEFAULT_DOCUMENT_BACKGROUND_COLOR)) {
      seen.add(normalizeColor(documentBackgroundColor));
    }

    let hasTableBlock = false;

    for (const section of sections) {
      // Sort block entries within the section by y, then x
      const blockEntries = Object.entries(section.blocksContent).sort(([, a], [, b]) => {
        const dy = a.blockConfig.y - b.blockConfig.y;
        return dy !== 0 ? dy : a.blockConfig.x - b.blockConfig.x;
      });

      for (const [blockId, block] of blockEntries) {
        const { type, content } = block;
        collectBlockStyleColors(type, blockStyles[blockId] ?? {}, seen);
        if (type === GridBlockType.TEXT && content) collectTipTapColors(content, textDefaults, seenNodeTypes, seen);
        if (type === GridBlockType.TABLE) hasTableBlock = true;
      }
    }

    if (hasTableBlock) collectTableColors(tableTextDefaults, tableRowStyles, seen);

    return Array.from(seen);
  }
);

export function getDocumentColors(): string[] {
  return selectDocumentColors(rootStore.getState());
}
